import axios from 'axios';
import { getCookie, setCookie, deleteCookie } from '../utils/cookies';
import { toast } from '../utils/toast';

const api = axios.create();

// Track if a refresh is in progress and queue any failed requests
let isRefreshing = false;
let failedRequestQueue = [];

const processQueue = (error, token = null) => {
    failedRequestQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });
    failedRequestQueue = [];
};

/**
 * Force logout — clears all auth state and redirects to login.
 * Called when refresh token is expired/invalid and session cannot be recovered.
 */
export const forceLogout = (message = null) => {
    deleteCookie('adminToken');
    deleteCookie('adminRefreshToken');
    deleteCookie('adminAuth');
    localStorage.clear();
    
    if (message) {
        sessionStorage.setItem('sessionTerminatedMessage', message);
    }
    
    window.location.href = '/login';
};

/**
 * Revoke tenant access — clears the current tenant selection but keeps the user logged in.
 * Redirects to the select-tenant page so they can choose another property.
 */
const revokeTenantAccess = (message = null) => {
    const authStr = getCookie('adminAuth');
    if (authStr) {
        try {
            const authData = JSON.parse(authStr);
            authData.tenantId = null;
            authData.tenantName = null;
            setCookie('adminAuth', JSON.stringify(authData));
        } catch(e) {}
    }
    
    if (message) {
        sessionStorage.setItem('tenantAccessRevokedMessage', message);
    }
    
    window.location.href = '/select-tenant';
};

/**
 * Proactive silent refresh — called on an interval from App.js to keep
 * the refresh token rotated before it expires. Also used by the 401 interceptor.
 * Returns true on success, false on failure.
 */
export const silentRefresh = async () => {
    // Don't run if a refresh is already in progress (from interceptor or another interval tick)
    if (isRefreshing) return false;

    const refreshToken = getCookie('adminRefreshToken');
    if (!refreshToken) return false;

    isRefreshing = true;
    try {
        const response = await axios.post(
            `${process.env.REACT_APP_API_BASE_URL_USER || ''}/api/v1/auth/refresh`,
            { refreshToken },
            {
                headers: {
                    'x-tenant-id': JSON.parse(getCookie('adminAuth') || '{}').tenantId || null
                }
            }
        );

        const newToken = response.data?.data?.token || response.data?.token;
        if (!newToken) throw new Error('No token in refresh response');

        // Store new access token
        setCookie('adminToken', newToken);

        const authData = JSON.parse(getCookie('adminAuth') || '{}');
        const roleName = authData.role?.name || authData.role || '';
        const isSuperAdmin = String(roleName).toUpperCase().replace(/[_\s-]+/g, "") === "SUPERADMIN";
        const isGlobalContext = !authData.tenantId || authData.tenantId === 'null';

        if (isSuperAdmin && isGlobalContext) {
            setCookie('adminGlobalToken', newToken);
        }

        // Store new rotated refresh token
        const newRefreshToken = response.data?.data?.refreshToken || response.data?.refreshToken;
        if (newRefreshToken) {
            setCookie('adminRefreshToken', newRefreshToken);
            if (isSuperAdmin && isGlobalContext) {
                setCookie('adminGlobalRefreshToken', newRefreshToken);
            }
        }

        // Update default header for subsequent requests
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

        return true;
    } catch (error) {
        console.error('Silent refresh failed:', error?.response?.data?.message || error.message);
        return false;
    } finally {
        isRefreshing = false;
    }
};

// Request interceptor to add common headers
api.interceptors.request.use(
    (config) => {
        const token = getCookie('adminToken');
        
        // Dynamically fetch the current active tenant value from your storage state container on demand
        const authData = JSON.parse(getCookie('adminAuth') || '{}');
        const activeTenantId = authData.tenantId;
        
        if (!config.headers['x-tenant-id'] && !config.headers['X-Tenant-Id']) {
            if (activeTenantId) {
                config.headers['x-tenant-id'] = String(activeTenantId);
            }
        }

        // Add Authorization token if it exists
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor — handles token refresh on 401
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const statusCode = error.response?.status;
        const errorMsg = error.response?.data?.message || '';
        const lowerMsg = errorMsg.toLowerCase();

        const isSwitchTenantRequest = originalRequest?.url?.includes('/auth/tenants/switch');
        const isAuthRefreshRequest = originalRequest?.url?.includes('/auth/refresh');
        const isAuthRequest =
            originalRequest?.url?.includes('/auth/login') ||
            originalRequest?.url?.includes('/auth/mfa/verify') ||
            originalRequest?.url?.includes('/auth/verify-mfa') ||
            originalRequest?.url?.includes('/auth/forgot-password') ||
            originalRequest?.url?.includes('/auth/verify-otp') ||
            originalRequest?.url?.includes('/auth/reset-password');

        // Immediate session invalidation if tenant is archived/inactive/suspended or session terminated
        if (!isSwitchTenantRequest && !isAuthRefreshRequest && !isAuthRequest) {
            if (statusCode === 401 || statusCode === 403) {
                if (lowerMsg.includes('archive') || 
                    lowerMsg.includes('inactive') || 
                    lowerMsg.includes('suspend') || 
                    lowerMsg.includes('terminate')) {
                    
                    forceLogout('Your session has been terminated because the hotel is no longer active.');
                    return Promise.reject(error);
                } else if (
                    statusCode === 403 && (
                        lowerMsg.includes('not assigned to this tenant') || 
                        lowerMsg.includes('removed from this tenant') || 
                        lowerMsg.includes('tenant access revoked') ||
                        lowerMsg.includes('no longer has access to this property')
                    )
                ) {
                    revokeTenantAccess('Your access to this property has been revoked by an administrator.');
                    return Promise.reject(error);
                }
            }
        }

        // Only attempt refresh on 401, and not on the refresh endpoint itself or auth requests
        if (
            statusCode === 401 &&
            !originalRequest._retry &&
            !isAuthRefreshRequest &&
            !isAuthRequest
        ) {
            const refreshToken = getCookie('adminRefreshToken');

            // No refresh token available — session is dead, force logout
            if (!refreshToken) {
                forceLogout();
                return Promise.reject(error);
            }

            // If already refreshing, queue this request and wait
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedRequestQueue.push({ resolve, reject });
                }).then((newToken) => {
                    // Mark queued requests as retried to prevent infinite loops
                    originalRequest._retry = true;
                    originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                    return api(originalRequest);
                }).catch((err) => Promise.reject(err));
            }

            // Mark as retried and start refresh
            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_API_BASE_URL_USER || ''}/api/v1/auth/refresh`,
                    { refreshToken },
                    {
                        headers: {
                            'x-tenant-id': JSON.parse(getCookie('adminAuth') || '{}').tenantId || null
                        }
                    }
                );

                const newToken = response.data?.data?.token || response.data?.token;
                if (!newToken) throw new Error('No token in refresh response');

                // Store new access token in cookies
                setCookie('adminToken', newToken);

                const authData = JSON.parse(getCookie('adminAuth') || '{}');
                const roleName = authData.role?.name || authData.role || '';
                const isSuperAdmin = String(roleName).toUpperCase().replace(/[_\s-]+/g, "") === "SUPERADMIN";
                const isGlobalContext = !authData.tenantId || authData.tenantId === 'null';

                if (isSuperAdmin && isGlobalContext) {
                    setCookie('adminGlobalToken', newToken);
                }

                // Store new rotated refresh token
                const newRefreshToken = response.data?.data?.refreshToken || response.data?.refreshToken;
                if (newRefreshToken) {
                    setCookie('adminRefreshToken', newRefreshToken);
                    if (isSuperAdmin && isGlobalContext) {
                        setCookie('adminGlobalRefreshToken', newRefreshToken);
                    }
                }

                // Update header and retry original + queued requests
                api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
                processQueue(null, newToken);

                return api(originalRequest);
            } catch (refreshError) {
                processQueue(refreshError, null);
                // Refresh failed — session is irrecoverable, force logout
                forceLogout();
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
            }
        }

        // Normalize error message from API response
        const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
        const customError = new Error(errorMessage);
        customError.response = error.response;
        customError.status = error.response?.status;
        return Promise.reject(customError);
    }
);

export default api;
